import { useEffect, useRef, useState } from 'react';

/**
 * Animates a number from 0 up to `target` on mount (and whenever `target` changes).
 * Returns the current in-flight value. Honors `prefers-reduced-motion` by snapping
 * straight to the target. useEffect is used here intentionally — this is a custom hook,
 * the one place the project allows it.
 */
export function useCountUp(target: number, durationMs = 900): number {
  const [value, setValue] = useState(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setValue(target);
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion || !Number.isFinite(target) || target === 0) {
      setValue(target);
      return;
    }

    const start = performance.now();
    // easeOutCubic — fast start, gentle settle.
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      const progress = Math.min((now - start) / durationMs, 1);
      setValue(target * ease(progress));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [target, durationMs]);

  return value;
}
